import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { toast } from "sonner";
import { AxiosError } from "axios";
import apiService from "../../services/api.services";

// Types
export interface Department {
  id: number;
  departmentName: string;
  hod: string; // Head of Department
  organizationId?: number;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface DepartmentFilters {
  departmentName?: string;
  organizationId?: number;
  page?: number;
  limit?: number;
}

export interface DepartmentsState {
  departments: Record<number, Department>;
  currentDepartment: Department | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  total: number;
  currentPage: number;
  lastFetched: Record<string, number>;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const createCacheKey = (params: DepartmentFilters): string => {
  return Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join("|");
};

const initialState: DepartmentsState = {
  departments: {},
  currentDepartment: null,
  status: "idle",
  error: null,
  total: 0,
  currentPage: 1,
  lastFetched: {},
};

// Async Thunks
export const fetchDepartments = createAsyncThunk(
  "departments/fetchDepartments",
  async (params: DepartmentFilters = {}, { getState, rejectWithValue }) => {
    try {
      // Check cache first
      const state = getState() as { departments: DepartmentsState };
      const cacheKey = createCacheKey(params);
      const lastFetched = state.departments.lastFetched[cacheKey] || 0;

      if (
        lastFetched > 0 &&
        Date.now() - lastFetched < CACHE_DURATION &&
        Object.keys(state.departments.departments).length > 0
      ) {
        // Return existing data if cache is valid
        return null;
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", String(params.page));
      if (params.limit) queryParams.append("limit", String(params.limit));
      if (params.departmentName)
        queryParams.append("departmentName", params.departmentName);
      if (params.organizationId)
        queryParams.append("organizationId", String(params.organizationId));

      const response = await apiService.get<{
        data: {
          departments: Department[];
          total: number;
        };
      }>(`/organizations/departments?${queryParams}`, {
        withCredentials: true,
      });

      return {
        departments: response.data.departments,
        total: response.data.total,
        currentPage: params.page || 1,
        cacheKey,
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to fetch departments"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to fetch departments",
      });
    }
  },
  {
    condition: (params, { getState }) => {
      const { departments } = getState() as { departments: DepartmentsState };
      // Don't fetch if we're already loading
      return departments.status !== "loading";
    },
  }
);

export const fetchDepartmentById = createAsyncThunk<Department, number>(
  "departments/fetchDepartmentById",
  async (id, { getState, rejectWithValue }) => {
    try {
      // Check if we already have this department
      const state = getState() as { departments: DepartmentsState };
      const existingDepartment = state.departments.departments[id];

      if (existingDepartment) {
        return existingDepartment;
      }

      const response = await apiService.get<{ data: Department }>(
        `/organizations/departments/${id}`
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to fetch department"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to fetch department",
      });
    }
  }
);

export const createDepartment = createAsyncThunk<
  Department,
  Omit<Department, "id" | "createdAt" | "updatedAt">
>(
  "departments/createDepartment",
  async (departmentData, { rejectWithValue }) => {
    try {
      const response = await apiService.post<{ data: Department }>(
        "/organizations/create-department",
        departmentData
      );

      toast.success("Department created successfully");
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to create department"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to create department",
      });
    }
  }
);

export const updateDepartment = createAsyncThunk<
  Department,
  { id: number; data: Partial<Department> }
>("departments/updateDepartment", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await apiService.put<{ data: Department }>(
      `/organizations/departments/${id}`,
      data
    );

    toast.success("Department updated successfully");
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(
      axiosError.response?.data?.message || "Failed to update department"
    );
    return rejectWithValue({
      message:
        axiosError.response?.data?.message || "Failed to update department",
    });
  }
});

export const deleteDepartment = createAsyncThunk<number, number>(
  "departments/deleteDepartment",
  async (id, { rejectWithValue }) => {
    try {
      await apiService.delete(`/organizations/departments/${id}`);
      toast.success("Department deleted successfully");
      return id;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to delete department"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to delete department",
      });
    }
  }
);

const departmentsSlice = createSlice({
  name: "departments",
  initialState,
  reducers: {
    resetDepartments: (state) => {
      state.departments = {};
      state.currentDepartment = null;
      state.status = "idle";
      state.error = null;
      state.total = 0;
      state.currentPage = 1;
      state.lastFetched = {};
    },
    setCurrentDepartment: (state, action: PayloadAction<Department>) => {
      state.currentDepartment = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Departments
      .addCase(fetchDepartments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.status = "succeeded";

        // Skip update if using cached data
        if (action.payload === null) {
          return;
        }

        const { departments, total, currentPage, cacheKey } = action.payload;

        // Update departments using normalized data structure
        state.departments = departments.reduce(
          (acc, department) => ({
            ...acc,
            [department.id]: department,
          }),
          state.departments
        );

        state.total = total;
        state.currentPage = currentPage;
        state.lastFetched[cacheKey] = Date.now();
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          (action.payload as { message: string })?.message ||
          "Failed to fetch departments";
      })

      // Fetch Department by ID
      .addCase(fetchDepartmentById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchDepartmentById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentDepartment = action.payload;
        state.departments[action.payload.id] = action.payload;
      })
      .addCase(fetchDepartmentById.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          (action.payload as { message: string })?.message ||
          "Failed to fetch department";
      })

      // Create Department
      .addCase(createDepartment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createDepartment.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.departments[action.payload.id] = action.payload;
        state.currentDepartment = action.payload;
      })
      .addCase(createDepartment.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          (action.payload as { message: string })?.message ||
          "Failed to create department";
      })

      // Update Department
      .addCase(updateDepartment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateDepartment.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.departments[action.payload.id] = action.payload;
        if (state.currentDepartment?.id === action.payload.id) {
          state.currentDepartment = action.payload;
        }
      })
      .addCase(updateDepartment.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          (action.payload as { message: string })?.message ||
          "Failed to update department";
      })

      // Delete Department
      .addCase(deleteDepartment.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteDepartment.fulfilled, (state, action) => {
        state.status = "succeeded";
        const departmentId = action.payload;
        delete state.departments[departmentId];
        if (state.currentDepartment?.id === departmentId) {
          state.currentDepartment = null;
        }
      })
      .addCase(deleteDepartment.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          (action.payload as { message: string })?.message ||
          "Failed to delete department";
      });
  },
});

// Selectors
export const selectAllDepartments = createSelector(
  [(state: { departments: DepartmentsState }) => state.departments.departments],
  (departments) => Object.values(departments)
);

export const selectDepartmentsStatus = (state: {
  departments: DepartmentsState;
}) => state.departments.status;

export const selectDepartmentsError = (state: {
  departments: DepartmentsState;
}) => state.departments.error;

export const selectDepartmentsPagination = createSelector(
  [(state: { departments: DepartmentsState }) => state.departments],
  (departments) => ({
    total: departments.total,
    currentPage: departments.currentPage,
  })
);

export const selectCurrentDepartment = (state: {
  departments: DepartmentsState;
}) => state.departments.currentDepartment;

export const selectDepartmentById = createSelector(
  [
    (state: { departments: DepartmentsState }) => state.departments.departments,
    (_, id: number) => id,
  ],
  (departments, id) => departments[id]
);

export const { resetDepartments, setCurrentDepartment } =
  departmentsSlice.actions;

export default departmentsSlice.reducer;
